'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import { buildDatUrl, cn, resolveSource } from '@workspace/lib';
import { SanityImage } from '@workspace/interfaces';
import { Icon, Picture } from '@/app/common';

interface GalleryViewProps {
  isOpen: boolean;
  gallerySlides: SanityImage[];
  selectedIndex: number;
  showNavigationArrows: boolean;
  navButtonClass: string;
  navIconSize: number;
  onSlide: (index: number) => void;
  onClose: () => void;
  visibleThumbIndices: number[];
  imgSize?: { width: number; height: number };
}

const getImageAlt = (image: SanityImage | undefined, fallback: string) =>
  image?.bynderImage?.alt || image?.alt || fallback;

export const GalleryView = ({
  isOpen,
  gallerySlides,
  selectedIndex,
  showNavigationArrows,
  navButtonClass,
  navIconSize,
  onSlide,
  onClose,
  visibleThumbIndices,
  imgSize,
}: GalleryViewProps) => {
  const [shouldRender, setShouldRender] = useState<boolean>(isOpen);
  const [visible, setVisible] = useState<boolean>(false);
  const originalBodyOverflow = useRef<string>('');

  const emblaOptions = useMemo(
    () => ({
      loop: gallerySlides.length > 1,
      align: 'start' as const,
      containScroll: 'trimSnaps' as const,
      duration: 24,
    }),
    [gallerySlides.length],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);

  const visibleDesktopThumbIndices = useMemo(() => {
    const maxVisible = 6;
    const totalSlides = gallerySlides.length;

    if (totalSlides <= maxVisible) {
      return Array.from({ length: totalSlides }, (_, i) => i);
    }

    const halfVisible = Math.floor(maxVisible / 2);
    let start = selectedIndex - halfVisible;
    let end = selectedIndex + halfVisible;

    if (start < 0) {
      start = 0;
      end = maxVisible;
    } else if (end >= totalSlides) {
      end = totalSlides;
      start = totalSlides - maxVisible;
    }

    return Array.from({ length: end - start }, (_, i) => start + i);
  }, [selectedIndex, gallerySlides.length]);

  const handlePrev = useCallback(() => {
    if (!emblaApi || gallerySlides.length <= 1) return;
    emblaApi.scrollPrev();
  }, [emblaApi, gallerySlides.length]);

  const handleNext = useCallback(() => {
    if (!emblaApi || gallerySlides.length <= 1) return;
    emblaApi.scrollNext();
  }, [emblaApi, gallerySlides.length]);

  const scrollToIndex = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    const handleSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      onSlide(index);
    };

    emblaApi.on('select', handleSelect);
    return () => {
      emblaApi.off('select', handleSelect);
    };
  }, [emblaApi, onSlide]);

  useEffect(() => {
    if (emblaApi && isOpen) {
      const current = emblaApi.selectedScrollSnap();
      if (current !== selectedIndex) {
        emblaApi.scrollTo(selectedIndex, true);
      }
    }
  }, [emblaApi, selectedIndex, isOpen]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit(emblaOptions);
  }, [emblaApi, emblaOptions]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      originalBodyOverflow.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }

    setVisible(false);
    const timeout = setTimeout(() => {
      setShouldRender(false);
      document.body.style.overflow = originalBodyOverflow.current || 'unset';
    }, 300);

    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = originalBodyOverflow.current || 'unset';
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    gallerySlides.forEach((item) => {
      const resolved = resolveSource(item);
      if (!resolved.baseUrl || !resolved.isBynder || resolved.isSvg) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = buildDatUrl(resolved.baseUrl, 100, 100);
    });
  }, [isOpen, gallerySlides]);

  if (!shouldRender) return null;

  const isNonSquare = !!imgSize && imgSize.width !== imgSize.height;
  const navButtonClasses = cn(
    'cursor-pointer z-20 pointer-events-auto border-0 p-3 rounded-full',
    isNonSquare ? 'bg-white/50 backdrop-blur-sm' : 'bg-transparent',
    navButtonClass,
  );

  const prevIndex = gallerySlides.length > 0 ? (selectedIndex - 1 + gallerySlides.length) % gallerySlides.length : 0;
  const nextIndex = gallerySlides.length > 0 ? (selectedIndex + 1) % gallerySlides.length : 0;

  const prevPreview = gallerySlides[prevIndex];
  const nextPreview = gallerySlides[nextIndex];

  return (
    <div
      className={cn(
        'fixed inset-0 bg-black! flex flex-col items-center justify-center  overflow-hidden transition-opacity ease-in duration-300 h-screen w-screen z-100',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      <Icon
        name="cancelIcon"
        size={24}
        onClick={onClose}
        className="absolute top-4 right-4 text-white text-3xl z-100 bg-none! border-none! cursor-pointer"
        aria-label="Close gallery"
      />

      {gallerySlides.length > 1 && (
        <>
          {gallerySlides.length > 2 || (gallerySlides.length === 2 && selectedIndex === 1) ? (
            <div
              className="hidden md:block absolute top-1/2 -translate-y-1/2 cursor-pointer transition-all duration-300 opacity-0 animate-preview-in pointer-events-auto -left-lg!"
              onClick={() => scrollToIndex(prevIndex)}
            >
              {prevPreview && (
                <Picture
                  image={prevPreview}
                  alt={getImageAlt(prevPreview, 'Previous image')}
                  desktopWidth={396}
                  desktopHeight={396}
                  imgClassName="size-[396px] object-cover opacity-70 hover:opacity-100 transition"
                />
              )}
            </div>
          ) : null}

          {gallerySlides.length > 2 || (gallerySlides.length === 2 && selectedIndex === 0) ? (
            <div
              className="hidden md:block absolute top-1/2 -translate-y-1/2 cursor-pointer transition-all duration-300 opacity-0 animate-preview-in pointer-events-auto -right-lg!"
              onClick={() => scrollToIndex(nextIndex)}
            >
              {nextPreview && (
                <Picture
                  image={nextPreview}
                  alt={getImageAlt(nextPreview, 'Next image')}
                  desktopWidth={396}
                  desktopHeight={396}
                  imgClassName="size-[396px] object-cover opacity-70 hover:opacity-100 transition"
                />
              )}
            </div>
          ) : null}
        </>
      )}

      <div
        className={cn(
          'relative mx-auto transition-transform ease-in duration-300',
          isNonSquare
            ? 'w-full max-w-3/4 h-screen flex flex-col gap-4 p-8 md:p-24 xl:py-48'
            : 'md:p-0 py-12 px-6 flex flex-col md:block h-screen md:h-auto justify-center md:justify-start',
        )}
        data-state={visible ? 'open' : 'closed'}
      >
        <div
          className={cn(
            'md:mx-auto overflow-hidden relative transition-transform ease-in duration-300 touch-pan-x',
            isNonSquare ? 'w-full flex-1 min-h-0' : 'md:w-[640px] md:h-[640px] w-full aspect-square md:flex-none',
            visible ? 'scale-100' : 'scale-95',
          )}
        >
          <div className="embla size-full overflow-hidden relative" ref={emblaRef}>
            <div className="embla__container flex size-full will-change-transform touch-pan-y select-none backface-hidden transform-[translateZ(0)]">
              {gallerySlides.map((item, idx) => (
                <div
                  key={`embla-slide-${idx}`}
                  className="embla__slide size-full relative shrink-0 grow-0 basis-full backface-hidden transform-[translateZ(0)] mr-3"
                >
                  <div className="absolute top-0 bottom-0 left-0 right-0 size-full backface-hidden transform-[translateZ(0)]">
                    <div className="relative size-full">
                      <Picture
                        image={item}
                        alt={getImageAlt(item, `gallery-${idx + 1}`)}
                        desktopWidth={imgSize?.width ?? 640}
                        desktopHeight={imgSize?.height ?? 640}
                        priority={idx === selectedIndex || idx === prevIndex || idx === nextIndex}
                        loading="eager"
                        imgClassName={cn('size-full', isNonSquare ? 'object-contain' : 'object-cover')}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {gallerySlides.length > 1 && showNavigationArrows && (
            <>
              <div className="absolute inset-0 flex items-center justify-between p-3 z-10 md:hidden pointer-events-none">
                <button type="button" className={navButtonClasses} aria-label="Previous image" onClick={handlePrev}>
                  <Icon name="ChevronLeft" className="text-primary" size={navIconSize} />
                </button>
                <button type="button" className={navButtonClasses} aria-label="Next image" onClick={handleNext}>
                  <Icon name="ChevronRight" className="text-primary" size={navIconSize} />
                </button>
              </div>

              <div className="absolute inset-0 items-center justify-between z-10 left-3 right-3 hidden md:flex pointer-events-none">
                <button type="button" className={navButtonClasses} aria-label="Previous image" onClick={handlePrev}>
                  <Icon name="ChevronLeft" className="text-primary" size={navIconSize} />
                </button>
                <button type="button" className={navButtonClasses} aria-label="Next image" onClick={handleNext}>
                  <Icon name="ChevronRight" className="text-primary" size={navIconSize} />
                </button>
              </div>
            </>
          )}
        </div>

        <div className="mt-4 md:mt-8 w-full">
          <div className="flex md:hidden justify-center gap-2">
            {visibleThumbIndices.map((idx) => {
              const item = gallerySlides[idx];
              if (!item) return null;
              return (
                <button
                  type="button"
                  key={`mobile-thumb-${idx}`}
                  onClick={() => scrollToIndex(idx)}
                  aria-label={`Show image ${idx + 1}`}
                  className={cn(
                    'flex-1 aspect-square shrink-0 overflow-hidden cursor-pointer relative z-10 border-0 bg-transparent p-0',
                    selectedIndex === idx ? 'ring-2 ring-primary' : '',
                  )}
                >
                  <Picture
                    image={item}
                    alt={getImageAlt(item, `thumbnail-${idx}`)}
                    desktopWidth={100}
                    desktopHeight={100}
                    loading="eager"
                    imgClassName="object-cover w-full h-full pointer-events-none"
                  />
                </button>
              );
            })}
          </div>

          <div className="hidden md:flex justify-center gap-2 flex-wrap">
            {visibleDesktopThumbIndices.map((idx) => {
              const item = gallerySlides[idx];
              if (!item) return null;
              return (
                <button
                  type="button"
                  key={`desk-thumb-${idx}`}
                  onClick={() => scrollToIndex(idx)}
                  aria-label={`Show image ${idx + 1}`}
                  className={cn(
                    'size-25 shrink-0 overflow-hidden cursor-pointer relative z-10 border-0 bg-transparent p-0',
                    selectedIndex === idx ? 'ring-2 ring-primary' : '',
                  )}
                >
                  <Picture
                    image={item}
                    alt={getImageAlt(item, `thumbnail-${idx}`)}
                    desktopWidth={100}
                    desktopHeight={100}
                    imgClassName="object-cover w-full h-full pointer-events-none"
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
